import { UserDto } from "@resume-studio/dto";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse, isAxiosError } from "axios";
import { useEffect } from "react";

import { ADMIN_USER_KEY } from "../../constants/query-keys";
import { axios } from "../../libs/axios";
import { useAuthStore } from "../../stores/auth";

// Source of truth for "is this browser session a logged-in admin?". The
// AuthGuard polls this on every mount; a 401 means "no admin session" and
// the guard routes to /login. We deliberately swallow 401 → undefined so
// React Query stays in a clean success state with no user.
//
// Named `fetchAdminSession` (not `fetchAdminUser`) to avoid the obvious
// collision with the admin users-management `fetchAdminUser` in
// `services/users.ts`. This one is the *current admin* — not look up
// an end-user record.
export const fetchAdminSession = async () => {
  try {
    const response = await axios.get<UserDto | undefined, AxiosResponse<UserDto | undefined>>(
      "/admin/auth/me",
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      return undefined;
    }
    throw error;
  }
};

// Renamed from `useAdminUser` to avoid the collision with the admin
// users-management hook (`services/users.ts`). This one is the *session*
// owner — the admin viewing the console — not "look up an end-user record".
export const useAdminSession = () => {
  const setUser = useAuthStore((state) => state.setUser);

  const {
    error,
    isPending: loading,
    data: user,
  } = useQuery({
    queryKey: ADMIN_USER_KEY,
    queryFn: fetchAdminSession,
  });

  useEffect(() => {
    setUser(user ?? null);
  }, [user, setUser]);

  return { user, loading, error };
};
